// Libraries
import axios from "axios";
import { err, ok, Result } from "neverthrow";
// Services
import { Logger, logging } from "@/framework/logger/local/LogManager";
import { IValidationService } from "@/framework/validation/IValidationService";
import { ValidationService } from "@/framework/validation/ValidationService";
import { IMediaService } from "./IMediaService";
import { GraphqlApi, IGraphqlApi } from "@/framework/network/gql/GraphqlApi";
// Objects
import { CloudinarySignature, CloudinarySignatureSchema } from "./CloudinarySignature";
import { CloudinaryUploadResponse, CloudinaryUploadResponseSchema } from "./CloudinaryUploadResponse";
import { CreateCloudinarySignatureDocument } from "@/framework/network/gql/generated/graphql";
import { CreateCloudinarySignatureMutation } from "../network/gql/generated/graphql.stg";
import z from "zod";

export type CreateCloudinarySignatureResult = z.infer<typeof CreateCloudinarySignatureResultSchema>;
export const CreateCloudinarySignatureResultSchema = z.object({
  apiKey: z.string(),
  cloudName: z.string(),
  folder: z.string(),
  signature: z.string(),
  timestamp: z.string(),
  uploadPreset: z.string(),
});

/**
 * Manages the file upload and resizing of media files or images in the cloud
 */
export class MediaService implements IMediaService {
  private readonly graphQL: IGraphqlApi;
  private readonly validation: IValidationService;
  private readonly cloudinaryBaseUrl: string;
  private logger: Logger;

  constructor(
    graphQL: IGraphqlApi = new GraphqlApi(),
    validation: IValidationService = new ValidationService(),
    cloudinaryBaseUrl: string = "https://api.cloudinary.com/v1_1/",
    logger: Logger = logging.getLogger("MediaService")
  ) {
    this.graphQL = graphQL;
    this.validation = validation;
    this.cloudinaryBaseUrl = cloudinaryBaseUrl;
    this.logger = logger;
  }

  uploadToCloudinary = async (
    file: File,
    uploadProgress: (event: ProgressEvent) => void | null
  ): Promise<Result<CloudinaryUploadResponse, Error>> => {
    let signature: CloudinarySignature;
    let variables = { folder: "" };
    const pdfPath = `pdf/${process.env.VUE_APP_ENV_NAME}/uploaded_files/`;
    if (this.isPdf(file)) {
      variables = { folder: pdfPath };
    }
    // Create cloudinary signature
    try {
      const res = await this.graphQL.runMutationAndValidate<
        CreateCloudinarySignatureMutation,
        CreateCloudinarySignatureResult
      >(CreateCloudinarySignatureDocument, CreateCloudinarySignatureResultSchema, variables);
      if (res.isErr()) {
        return err(res.error);
      }

      signature = res.value;
    } catch (unknownError) {
      this.logger.error(unknownError);
      let error = unknownError as Error;
      return err(error);
    }
    // Validate signature response from Kotozna server
    const signatureResult = this.validation.validate(signature, CloudinarySignatureSchema);
    if (signatureResult.isErr()) {
      return err(signatureResult.error);
    }
    // Construct url and form data
    let url = this.cloudinaryBaseUrl + signatureResult.value.cloudName + "/auto/upload";
    const formData = this.createFormData(file, signature, pdfPath);
    if (formData.isErr()) {
      return err(formData.error);
    }
    const options = {
      headers: {
        "X-Requested-With": "XMLHttpRequest",
      },
    };
    // Upload file
    let uploadResult: CloudinaryUploadResponse;
    try {
      const axiosResult = await axios.post<CloudinaryUploadResponse>(url, formData.value, options);
      // Validate upload response from Cloudinary server
      const uploadValidation = this.validation.validate(axiosResult, CloudinaryUploadResponseSchema);
      if (uploadValidation.isErr()) {
        this.logger.error(uploadValidation.error);
        return err(uploadValidation.error);
      }
      uploadResult = axiosResult as unknown as CloudinaryUploadResponse;
    } catch (unknownError) {
      this.logger.error(unknownError);
      let error = unknownError as Error;
      return err(error);
    }
    return ok(uploadResult);
  };

  resizeCloudinaryImage = (url: string, width: number): string | null => {
    if (!url || !width) {
      this.logger.error("resizeCloudinaryImage - No url or width given");
      return null;
    }
    return url.replace("/image/upload/", `/image/upload/w_${width},fl_progressive/`);
  };

  resizeCloudinaryVideo = (url: string, width: number, quality: number): string | null => {
    if (!url || !width || !quality) {
      this.logger.error(
        `resizeCloudinaryVideo - No url, width or quality given. \n URL: ${url} \n Widht: ${width} \n Quality: ${quality}`
      );
      return null;
    }
    return url.replace("/video/upload/", `/video/upload/w_${width},q_${quality}/`);
  };

  /**
   * Private helper to create the form data for the file upload
   * @param file File to upload
   * @param signature Cloudinary signature
   * @param pdfPath Cloudinary folder path to store pdf files
   * @returns FormData object
   */
  private createFormData = (file: File, signature: CloudinarySignature, pdfPath: string): Result<FormData, Error> => {
    const data = new FormData();
    data.append("upload_preset", signature.uploadPreset);
    data.append("signature", signature.signature);
    data.append("api_key", signature.apiKey);
    data.append("timestamp", signature.timestamp);
    data.append("file", file);
    if (this.isPdf(file)) {
      data.append("folder", pdfPath);
    }
    return ok(data);
  };

  /**
   * Helper to check if a file is of type PDF
   * @param file Input file
   * @returns boolean
   */
  private isPdf = (file: File): boolean => {
    return file.name.endsWith("pdf");
  };
}
